import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';

import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { BASE_PATH as BOOKING_PATH, ApiModule as BookingApiModule } from '@chicilon/angular-booking-service';
import { BASE_PATH as BUILDING_PATH, ApiModule as BuildingApiModule } from '@chicilon/angular-building-service';
import { BASE_PATH as CONTRACT_PATH, ApiModule as ContratApiModule } from '@chicilon/angular-contract-service';
import { BASE_PATH as CRM_PATH, ApiModule as CrmApiModule } from '@chicilon/angular-crm-service';
import { BASE_PATH as EXPLOIT_PATH, ApiModule as ExploitApiModule } from '@chicilon/angular-exploit-service';
import { BASE_PATH as HRM_PATH, ApiModule as HrmApiModule } from '@chicilon/angular-hrm-service';
import {
  BASE_PATH as NOTIFICATION_PATH,
  ApiModule as NotificationApiModule
} from '@chicilon/angular-notification-service';
import { BASE_PATH as STORAGE_PATH, ApiModule as StorageApiModule } from '@chicilon/angular-storage-service';
import { BASE_PATH as USER_BASE_PATH, ApiModule as UserApiModule } from '@chicilon/angular-user-service';
import { authInterceptor, unauthErrorInterceptor } from '@core/interceptors/auth.interceptor';
import { provideStore } from '@ngrx/store';
import { routes } from './app.routes';
import { MyPreset } from './mypreset';

import { AuthService } from '@core/services/auth.service';
import { ChunkErrorRecoveryService } from '@core/services/chunk-error-recovery.service';
import { GlobalErrorHandler } from '@core/services/global-error-handler';
import { CustomTitleStrategy } from '@core/strategies/custom-title.strategy';
import { environment } from '@environments/environment';
import { provideEffects } from '@ngrx/effects';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { LanguageService } from '@shared/service/language.service';
import { AuthEffects } from '@state/auth/auth.effects';
import { authReducer } from '@state/auth/auth.reducers';
import { SystemEffects } from '@state/system/system.effects';
import { systemReducer } from '@state/system/system.reducers';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { MessageService } from 'primeng/api';
import { firstValueFrom, tap } from 'rxjs';

function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

const provideTranslation = () => ({
  defaultLanguage: localStorage.getItem('lang') || 'vi',
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient]
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    provideZoneChangeDetection({
      eventCoalescing: true
    }),
    provideAnimations(),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideHttpClient(withInterceptors([authInterceptor, unauthErrorInterceptor])),
    provideStore({ auth: authReducer, system: systemReducer }),
    provideEffects(AuthEffects, SystemEffects),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities'
          }
        }
      }
    }),
    importProvidersFrom([
      TranslateModule.forRoot(provideTranslation()),
      BookingApiModule,
      BuildingApiModule,
      StorageApiModule,
      UserApiModule,
      ContratApiModule,
      CrmApiModule,
      HrmApiModule,
      ExploitApiModule,
      NotificationApiModule
    ]),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    // Đăng ký sớm, trước initializer gọi API bên dưới, để bắt được lỗi chunk
    // ngay từ lần điều hướng đầu tiên.
    provideAppInitializer(() => {
      inject(ChunkErrorRecoveryService).init();
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const languageService = inject(LanguageService);
      return firstValueFrom(translate.use(languageService.getCurrentLanguage()));
    }),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const ps = inject(NgxPermissionsService);
      return firstValueFrom(
        authService.getMyPermission().pipe(
          tap(res => {
            if (res.data) {
              ps.loadPermissions(res.data);
            }
          })
        )
      );
    }),
    {
      provide: BOOKING_PATH,
      useValue: `${environment.BASE_URL}/booking`
    },
    {
      provide: BUILDING_PATH,
      useValue: `${environment.BASE_URL}/building`
    },
    {
      provide: USER_BASE_PATH,
      useValue: `${environment.BASE_URL}/user`
    },
    {
      provide: STORAGE_PATH,
      useValue: `${environment.BASE_URL}/storage`
    },
    {
      provide: CONTRACT_PATH,
      useValue: `${environment.BASE_URL}/contract`
    },
    {
      provide: CRM_PATH,
      useValue: `${environment.BASE_URL}/crm`
    },
    {
      provide: HRM_PATH,
      useValue: `${environment.BASE_URL}/hrm`
    },
    {
      provide: EXPLOIT_PATH,
      useValue: `${environment.BASE_URL}/exploit`
    },
    {
      provide: NOTIFICATION_PATH,
      useValue: `${environment.BASE_URL}/notification`
    },
    CustomCurrencyPipe,
    MessageService
  ]
};
